import React from "react";

import { Box, Button, Grid, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import DownloadReportIcon from "@/components/Icons/DownloadReportIcon";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import MealRatingReportDownloadDialog from "./MealRatingReportDownloadDialog";
import MealRatingReportItem from "./MealRatingReportItem";
import { downloadReportPDF } from "../utils/downloadPdfReport";
import { ClientReportPageParams } from "../utils/types";
import { useMealRatingReport } from "../utils/useMealRatingReport";

const useStyles = makeStyles()(() => ({
  weeklyRating: {
    color: "#363E45",
    fontFamily: "Mulish",
    fontSize: 20,
    fontWeight: 700,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  downloadReport: {
    fontFamily: "Mulish",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: -0.5,
  },
}));

const MealRatingReport: React.FC = () => {
  const { classes } = useStyles();
  const { clientId, date } = useParams<ClientReportPageParams>();
  const snackbar = useGlobalSnackbar();
  const downloadPDFDialog = useDialogState();
  const data = useMealRatingReport();

  const hasData = Object.keys(data).length > 0;

  const handleDownloadReport = async (startDate: Date, endDate: Date) => {
    if (!clientId) return;

    downloadPDFDialog.hide();
    try {
      await downloadReportPDF(clientId, startDate, endDate);
    } catch (e) {
      snackbar.open(
        displayError(e, "Unable to save as PDF. Please try again later.")
      );
    }
  };

  const renderMealReportItems = () =>
    hasData &&
    Object.entries(data).map(([key, value]) => {
      const item = { [key]: value };

      return (
        <Grid key={key} item xs={true}>
          <MealRatingReportItem item={item} />
        </Grid>
      );
    });

  return (
    <Box padding={4}>
      <Box className={classes.header}>
        <Typography className={classes.weeklyRating}>
          Meal Rating Report
        </Typography>
        <Button
          disabled={!hasData}
          className={classes.downloadReport}
          startIcon={<DownloadReportIcon />}
          onClick={downloadPDFDialog.show}
        >
          Download Report
        </Button>
      </Box>
      <Grid container spacing={2} justifyContent="center">
        {renderMealReportItems()}
      </Grid>
      <MealRatingReportDownloadDialog
        selectedDate={date || formatISODate(new Date())}
        isOpen={downloadPDFDialog.shown}
        onClose={downloadPDFDialog.hide}
        onDownload={handleDownloadReport}
      />
    </Box>
  );
};

export default MealRatingReport;
